import moment from 'moment-timezone';

import HomeService, { metricsPaths } from './home.service';
import ApiService from '@shared/services/api.service';
import { UserDate } from '@shared/services/userDate.service';
import { formatUtcTime } from '@shared/helpers/utils';

const PLAN_TIERS = [
  { key: 'GOLD', label: 'Gold' },
  { key: 'SILVER', label: 'Silver' },
  { key: 'BRONZE', label: 'Bronze' },
  { key: 'TRIAL', label: 'Trial' }
];

const DATE_FMT = 'YYYY-MM-DD';

const FREQUENCY_LABELS = { once: 'Once', daily: 'Daily', weekly: 'Weekly' };

// Crawl timestamps are UTC, but some carry an explicit offset and some no zone at
// all — appending `Z` to the former would make it unparseable. `0000-00-00 ...` is
// MySQL's "never" for domain crawls, so treat anything unparseable as absent.
const utcMoment = (value) => {
  if (!value) return null;
  const raw = String(value);
  const m = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
    ? moment.parseZone(raw).local()
    : moment.utc(formatUtcTime(raw)).local();
  return m.isValid() && m.year() > 1970 ? m : null;
};

// Midnight today in the user's profile timezone — the frame the API buckets days
// in, so requested ranges and the keys we join on agree.
const userStartOfDay = () => moment.tz(UserDate.userTimezone).startOf('day');

// Inclusive `days`-day window ending today.
const lastNDaysRange = (days) => {
  const end = userStartOfDay();
  const start = end.clone().subtract(days - 1, 'days');
  return { start: start.format(DATE_FMT), end: end.format(DATE_FMT) };
};

// Webpage id off a metrics `url` object (mapped `id` or raw `urlID`).
const urlId = (url) => {
  if (!url) return null;
  return url.id != null ? url.id : (url.urlID != null ? url.urlID : null);
};

// `{ 'YYYY-M-D': count }` -> Map keyed by canonical `YYYY-MM-DD`.
const toDayCountMap = (raw) => {
  const map = new Map();
  Object.entries(raw || {}).forEach(([date, count]) => {
    const key = moment(date, ['YYYY-MM-DD', 'YYYY-M-D']).format(DATE_FMT);
    map.set(key, Number(count) || 0);
  });
  return map;
};

const DashboardService = {
  // Monitored URLs grouped by health status.
  async fetchPortfolio(tagIds = [], planTypes = []) {
    const raw = await HomeService.fetchCountsNumberOfURLsByHealthStatus(tagIds, planTypes);
    const groups = Object.entries(raw || {})
      .map(([status, count]) => ({ status, count: Number(count) || 0 }))
      .filter((g) => g.count > 0);
    const total = groups.reduce((sum, g) => sum + g.count, 0);
    return { total, groups };
  },

  // Archived URLs, from the listing's pagination total. null when not knowable.
  async fetchArchivedUrlCount(tagIds = []) {
    const res = await HomeService.fetchArchivedUrlCount(tagIds);
    const total = Number(res && res.meta && res.meta.total);
    return Number.isFinite(total) ? total : null;
  },

  // Today's alert count + delta vs yesterday.
  async fetchTodaysAlerts(tagIds = [], planTypes = []) {
    const today = userStartOfDay();
    const yesterday = today.clone().subtract(1, 'day');
    const raw = await HomeService.fetchCountsNumberOfIncidentsPerDay(
      yesterday.format(DATE_FMT),
      today.format(DATE_FMT),
      tagIds,
      planTypes
    );
    const map = toDayCountMap(raw);
    const total = map.get(today.format(DATE_FMT)) || 0;
    const prev = map.get(yesterday.format(DATE_FMT)) || 0;
    const deltaPct = prev > 0 ? Math.round(((total - prev) / prev) * 100) : null;
    return { total, deltaPct };
  },

  // Severity tally over an N-day window.
  async fetchSeverityTally(days = 7, tagIds = [], planTypes = []) {
    const { start, end } = lastNDaysRange(days);
    const raw = await HomeService.fetchIncidentCountBySeverity(start, end, tagIds, planTypes);
    const high = Number((raw && raw.high) || 0);
    const medium = Number((raw && raw.medium) || 0);
    const low = Number((raw && raw.low) || 0);
    const total = high + medium + low;
    return { high, medium, low, total, hasSeverity: total > 0 };
  },

  // Per-day total counts, gap-filled to a continuous series.
  async fetchTotalTrend(days = 7, tagIds = [], planTypes = []) {
    const { start, end } = lastNDaysRange(days);
    const raw = await HomeService.fetchCountsNumberOfIncidentsPerDay(start, end, tagIds, planTypes);
    const map = toDayCountMap(raw);
    const today = userStartOfDay();
    const series = [];
    for (let i = days - 1; i >= 0; i--) {
      const key = today.clone().subtract(i, 'days').format(DATE_FMT);
      series.push({ date: key, count: map.get(key) || 0 });
    }
    return { series };
  },

  // Per-day HIGH/MEDIUM/LOW counts, gap-filled to a continuous series.
  async fetchSeverityTrend(days = 7, tagIds = [], planTypes = []) {
    const { start, end } = lastNDaysRange(days);
    const raw = await HomeService.fetchIncidentCountByDaySeverity(start, end, tagIds, planTypes);

    const byDay = new Map();
    Object.entries(raw || {}).forEach(([date, counts]) => {
      const key = moment(date, ['YYYY-MM-DD', 'YYYY-M-D']).format(DATE_FMT);
      byDay.set(key, {
        high: Number((counts && counts.high) || 0),
        medium: Number((counts && counts.medium) || 0),
        low: Number((counts && counts.low) || 0)
      });
    });

    const today = userStartOfDay();
    const series = [];
    for (let i = days - 1; i >= 0; i--) {
      const key = today.clone().subtract(i, 'days').format(DATE_FMT);
      const b = byDay.get(key) || { high: 0, medium: 0, low: 0 };
      series.push({ date: key, high: b.high, medium: b.medium, low: b.low });
    }
    return { series };
  },

  // Top URLs by total alerts over N days (top 20).
  async fetchTopNoisyUrls(days = 7, tagIds = [], planTypes = []) {
    const { start, end } = lastNDaysRange(days);
    const raw = await HomeService.fetchCountsNumberOfIncidentsPerUrl(start, end, tagIds, planTypes);
    return (raw || [])
      .map((row) => ({
        id: urlId(row.url),
        name: (row.url && (row.url.name || row.url.url)) || 'Unknown',
        url: (row.url && row.url.url) || '',
        count: Number(row.count) || 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  },

  // Top 20 URLs by selected severities over N days (server-sorted).
  async fetchTopPagesBySeverity(days = 7, tagIds = [], planTypes = [], levels = ['HIGH', 'MEDIUM', 'LOW']) {
    const { start, end } = lastNDaysRange(days);
    const raw = await HomeService.fetchIncidentCountByUrlSeverity(start, end, tagIds, planTypes, levels);
    const pages = (raw || []).map((row) => ({
      id: urlId(row.url),
      name: (row.url && (row.url.name || row.url.url)) || 'Unknown',
      url: (row.url && row.url.url) || '',
      high: Number(row.high) || 0,
      medium: Number(row.medium) || 0,
      low: Number(row.low) || 0
    }));
    return { pages, sampled: false };
  },

  // Top 10 FQDNs + top 10 tags by alerts over N days (account-wide).
  async fetchFqdnTagBreakdown(days = 7) {
    const { start, end } = lastNDaysRange(days);
    const raw = (await HomeService.fetchIncidentCountByFqdnTag(start, end)) || {};
    const fqdns = (Array.isArray(raw.fqdns) ? raw.fqdns : []).map((r) => ({
      name: r.name || 'Unknown',
      count: Number(r.count) || 0
    }));
    const tags = (Array.isArray(raw.tags) ? raw.tags : []).map((r) => ({
      id: r.id,
      name: r.name || 'Untitled',
      count: Number(r.count) || 0
    }));
    return { fqdns, tags };
  },

  // Monitored URLs currently inside a blackout window. Both counts are filtered
  // server-side, so they can't disagree.
  async fetchBlackoutCoverage(tagIds = [], planTypes = []) {
    const raw = (await HomeService.fetchBlackoutCoverage(tagIds, planTypes)) || {};
    return {
      total_urls: Number(raw.total_urls) || 0,
      in_progress: Number(raw.in_progress) || 0
    };
  },

  // Next `limit` scheduled website crawls. next_run_at is stored UTC and already
  // excludes inactive schedules server-side.
  async fetchUpcomingCrawls(limit = 5) {
    const res = await HomeService.fetchCrawlSchedules();
    const rows = Array.isArray(res && res.data) ? res.data : [];
    const now = moment();

    return rows
      .map((row) => ({
        id: row.id,
        // A schedule can cover several domains; show them as one entry.
        name: (Array.isArray(row.domains) ? row.domains : []).join(', ') || 'Unknown',
        frequency: FREQUENCY_LABELS[row.frequency] || row.frequency || '',
        nextRun: utcMoment(row.next_run_at)
      }))
      .filter((r) => r.nextRun && r.nextRun.isSameOrAfter(now))
      .sort((a, b) => a.nextRun.valueOf() - b.nextRun.valueOf())
      .slice(0, limit);
  },

  // Next `limit` scheduled domain crawls. These carry no next-run column, so it
  // is derived from last run + interval (or the start date when never run); a
  // derived time already past means the run is due rather than upcoming.
  async fetchUpcomingDomainCrawls(limit = 5) {
    const rows = await HomeService.fetchDomainCrawlSchedules();
    const now = moment();

    return (Array.isArray(rows) ? rows : [])
      .map((row) => {
        const lastRun = utcMoment(row.last_run_date);
        const interval = moment.duration(row.interval || 'P1D');
        const nextRun = lastRun
          ? moment(lastRun).add(interval)
          : utcMoment(row.start_date);
        return {
          id: row.id,
          name: row.name || row.seed_url || 'Unknown',
          frequency: row.interval === 'P7D' ? 'Weekly' : 'Daily',
          nextRun,
          overdue: Boolean(nextRun && nextRun.isBefore(now))
        };
      })
      .filter((r) => r.nextRun && r.nextRun.isValid())
      .sort((a, b) => a.nextRun.valueOf() - b.nextRun.valueOf())
      .slice(0, limit);
  },

  // Per plan tier + a combined domain bar (domain URLs excluded from tiers).
  async fetchPlanUtilisation() {
    const res = (await HomeService.fetchPlanUtilisation()) || {};
    const plans = res.plans || {};
    const d = res.domain || {};
    // Count today; tolerate a list, since a false reading un-hides the caps below.
    const subs = d.subscriptions;
    const domain = {
      used: Number(d.count) || 0,
      limit: Number(d.limit) || 0,
      subscribed: Array.isArray(subs) ? subs.length > 0 : Number(subs) > 0
    };
    const tiers = PLAN_TIERS.map(({ key, label }) => {
      const row = plans[key] || {};
      const limit = Number(row.limit) || 0;
      const used = Number(row.count) || 0;
      const entitlement = Number(row.entitlement) || 0;
      return { key: key.toLowerCase(), label, used, limit, entitlement };
    })
      // On a domain account the tier limit is just the account cap, so it earns a
      // bar only once used (or contracted). Tier-only accounts always show theirs.
      .filter((t) => t.used > 0 || t.entitlement > 0 || (t.limit > 0 && !domain.subscribed));
    return { tiers, domain };
  },

  // Batch descriptor builders. Each `path` must match the widget's own cache key
  // (both built from `metricsPaths`) so the primed batch response is a cache hit.
  resources: {
    portfolio: (tagIds = [], planTypes = []) => ({
      path: metricsPaths.urlCountByStatus(tagIds, planTypes),
      type: 'url_count_by_status'
    }),
    today: (tagIds = [], planTypes = []) => {
      // Must match fetchTodaysAlerts' range exactly, or the primed key is a miss.
      const today = userStartOfDay();
      const start = today.clone().subtract(1, 'day').format(DATE_FMT);
      const end = today.format(DATE_FMT);
      return {
        path: metricsPaths.incidentCountByDay(start, end, tagIds, planTypes),
        type: 'incident_count_by_day',
        start,
        end
      };
    },
    severityToday: (tagIds = [], planTypes = []) => {
      const { start, end } = lastNDaysRange(1);
      return {
        path: metricsPaths.incidentCountBySeverity(start, end, tagIds, planTypes),
        type: 'incident_count_by_severity',
        start,
        end
      };
    },
    blackout: (tagIds = [], planTypes = []) => ({
      path: metricsPaths.blackoutCoverage(tagIds, planTypes),
      type: 'blackout_coverage'
    }),
    totalTrend: (days = 7, tagIds = [], planTypes = []) => {
      const { start, end } = lastNDaysRange(days);
      return {
        path: metricsPaths.incidentCountByDay(start, end, tagIds, planTypes),
        type: 'incident_count_by_day',
        start,
        end
      };
    },
    severityTrend: (days = 7, tagIds = [], planTypes = []) => {
      const { start, end } = lastNDaysRange(days);
      return {
        path: metricsPaths.incidentCountByDaySeverity(start, end, tagIds, planTypes),
        type: 'incident_count_by_day_severity',
        start,
        end
      };
    },
    topNoisy: (days = 7, tagIds = [], planTypes = []) => {
      const { start, end } = lastNDaysRange(days);
      return {
        path: metricsPaths.incidentCountByUrl(start, end, tagIds, planTypes),
        type: 'incident_count_by_url',
        start,
        end
      };
    },
    topPages: (days = 7, tagIds = [], planTypes = [], levels = ['HIGH', 'MEDIUM', 'LOW']) => {
      const { start, end } = lastNDaysRange(days);
      return {
        path: metricsPaths.incidentCountByUrlSeverity(start, end, tagIds, planTypes, levels),
        type: 'incident_count_by_url_severity',
        start,
        end,
        levels
      };
    },
    planUtil: () => ({ path: metricsPaths.planUtilisation(), type: 'plan_utilisation' }),
    fqdnTag: (days = 7) => {
      const { start, end } = lastNDaysRange(days);
      return {
        path: metricsPaths.incidentCountByFqdnTag(start, end),
        type: 'incident_count_by_fqdn_tag',
        start,
        end
      };
    }
  },

  // POST descriptors in one request -> { results: [{ path, type, data }] }.
  // Tag/plan filters are batch-wide; `levels` rides on the item that needs it.
  async loadDashboardBatch(items = [], tagIds = [], planTypes = []) {
    return ApiService.post('metrics/dashboard', {
      tag_ids: tagIds,
      plan_types: planTypes,
      items: items.map((i) => ({
        path: i.path,
        type: i.type,
        start: i.start || null,
        end: i.end || null,
        levels: i.levels || null
      }))
    });
  }
};

export default DashboardService;
